import { AlertTriangle, ArrowUpRight, CheckCircle2, Home, Layers, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { OrgDashboardSnapshot } from '@/features/dashboard/dashboardTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';

type OrgDashboardProps = {
  fullName: string;
  snapshot: OrgDashboardSnapshot;
  currentOrgContext: {
    organizationName?: string;
    roleLabel: string;
    organizationDetailsHref?: string;
  } | null;
};

function KpiCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  title: string;
  value: number;
  description: string;
  icon: typeof Home;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="text-muted-foreground h-4 w-4" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-muted-foreground text-xs">{description}</p>
      </CardContent>
    </Card>
  );
}

export function OrgDashboard({ fullName, snapshot, currentOrgContext }: OrgDashboardProps) {
  const { hasPermission } = usePermissions();
  const canViewTemplates = hasPermission(Permissions.TemplatesRead);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome back, ${fullName}`}
        description={
          currentOrgContext ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {currentOrgContext.organizationName ? (
                <span className="text-foreground font-medium">{currentOrgContext.organizationName}</span>
              ) : null}
              {currentOrgContext.organizationName ? <span className="text-muted-foreground">•</span> : null}
              <span className="text-muted-foreground capitalize">{currentOrgContext.roleLabel}</span>
              {currentOrgContext.organizationDetailsHref ? (
                <Button asChild variant="ghost" size="sm" className="h-6 px-1">
                  <Link to={currentOrgContext.organizationDetailsHref} aria-label="Open organization details">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Properties"
          value={snapshot.activeProperties}
          description="Loaded properties in your scope."
          icon={Home}
        />
        <KpiCard
          title="Templates"
          value={snapshot.templatesCount}
          description="Templates currently in cache."
          icon={Layers}
        />
        <KpiCard
          title="Organization Members"
          value={snapshot.orgMembersCount}
          description="Members loaded for the active organization."
          icon={Users}
        />
        <KpiCard
          title="Capabilities"
          value={snapshot.orgCapabilitiesCount}
          description="Permissions enabled for active organization."
          icon={CheckCircle2}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Placeholder feed until snapshot API is connected.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {snapshot.recentActivity.map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-3 rounded-md border p-3">
                <div className="space-y-1">
                  <p className="text-sm font-medium">{item.title}</p>
                  {item.description ? <p className="text-muted-foreground text-xs">{item.description}</p> : null}
                </div>
                <div className="text-right">
                  <p className="text-muted-foreground text-xs">{item.timestampLabel}</p>
                  {item.href ? (
                    <Button asChild variant="link" size="sm" className="h-auto p-0">
                      <Link to={item.href}>Open</Link>
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Attention</CardTitle>
            <CardDescription>Operational items to review this week.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {snapshot.attentionItems.map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-2 rounded-md border p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle
                    className={`mt-0.5 h-4 w-4 ${
                      item.tone === 'critical'
                        ? 'text-red-600'
                        : item.tone === 'warning'
                          ? 'text-amber-600'
                          : 'text-sky-600'
                    }`}
                  />
                  <p className="text-sm">{item.label}</p>
                </div>
                <Badge variant={item.tone === 'critical' ? 'destructive' : 'secondary'}>{item.tone}</Badge>
              </div>
            ))}
            {canViewTemplates ? (
              <div className="grid gap-2 pt-2">
                {canViewTemplates ? (
                  <Button asChild variant="outline" size="sm">
                    <Link to="/templates">View Templates</Link>
                  </Button>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
